import { ElementType } from 'react';

import { BANK_ACCOUNTS } from '~/app/pages/BankAccounts';
import { CARD_ACCOUNTS } from '~/app/pages/Cards/Accounts';
import { CARDS } from '~/app/pages/Cards/Cards';
import { CARD_DISPUTES } from '~/app/pages/Cards/Disputes';
import { CARD_PROGRAMS } from '~/app/pages/Cards/Programs';
import { CARD_TOKENS } from '~/app/pages/Cards/Tokens';
import { CARD_TRANSACTIONS } from '~/app/pages/Cards/Transactions';
import { PageContact } from '~/app/pages/Contact';
import { COUNTERPARTIES } from '~/app/pages/Counterparties';
import { DOCUMENTS } from '~/app/pages/Documents';
import { ENTITIES } from '~/app/pages/Entities';
import { PageEvents } from '~/app/pages/Events';
import { PageHomeOverview } from '~/app/pages/Home/Overview';
import { LEGAL } from '~/app/pages/Legal';
import { LOANS } from '~/app/pages/Loans';
import { PLATFORM } from '~/app/pages/Platform';
import { TRANSFERS } from '~/app/pages/Transfers';
import { USER } from '~/app/pages/User';
import { PageWebhookEvents } from '~/app/pages/WebhookEvents';
import type { Action } from '~/lib/authz/eval';
import { FeatureFlag } from '~/lib/flags';

export type PageTypeLevel = 'top' | 'nested';
export type HeaderBorderMode = true | false | 'onScroll';
type PageTypeVisibility = 'sidebar' | 'tab' | 'hidden';

interface PageTypeUrl {
  route: string;
  redirect?: string;
}

interface PageTypeBase<L extends PageTypeLevel> {
  title: string;
  url: string | PageTypeUrl;
  visibility?: PageTypeVisibility | (L extends 'nested' ? 'inherit' : never);
  showInBreadcrumbs?: boolean;
  // Hide the breadcrumb until the page calls useBreadcrumbTitle to provide one.
  dynamicBreadcrumb?: boolean;
  routeElement?: ElementType;
  permission?: Action;
  hasHeaderBorder?: HeaderBorderMode;
  rules?: {
    allowWithoutPlatformAccess?: boolean;
    requireFeatureFlag?: FeatureFlag;
  };
}

interface PageWithoutChildPages<L extends PageTypeLevel> extends PageTypeBase<L> {
  element: ElementType;
}

interface PageWithChildPages<L extends PageTypeLevel> extends PageTypeBase<L> {
  childPages: PageType<'nested'>[];
}

interface PageWithChildPagesWithoutRedirect<L extends PageTypeLevel> extends PageWithChildPages<L> {
  element?: ElementType; // optional -> implies redirect to first child when absent
}

export type PageType<L extends PageTypeLevel> = PageWithoutChildPages<L> | PageWithChildPagesWithoutRedirect<L>;

export const Sitemap: PageType<'top'>[] = [
  {
    title: 'Home',
    url: '/',
    element: PageHomeOverview,
    visibility: 'sidebar',
    hasHeaderBorder: 'onScroll',
  },
  {
    title: 'Platform',
    url: '/platform',
    visibility: 'sidebar',
    childPages: [
      {
        title: 'Settings',
        url: 'settings',
        element: PLATFORM.PlatformSettings,
        visibility: 'tab',
        showInBreadcrumbs: false,
        permission: { permission: 'platformInfo', permissionLevel: 'read' },
      },
      {
        title: 'Roles',
        url: 'roles',
        element: PLATFORM.Roles,
        visibility: 'tab',
        hasHeaderBorder: 'onScroll',
        permission: { permission: 'users', permissionLevel: 'read' },
        childPages: [
          {
            title: 'Create',
            url: 'create',
            element: PLATFORM.CreateRole,
            permission: { permission: 'users', permissionLevel: 'write' },
          },
          {
            title: 'Edit',
            url: ':roleId',
            element: PLATFORM.EditRole,
            permission: { permission: 'users', permissionLevel: 'read' },
            dynamicBreadcrumb: true,
          },
        ],
      },
      {
        title: 'Team',
        url: 'team',
        element: PLATFORM.Team,
        visibility: 'tab',
        hasHeaderBorder: true,
        permission: { permission: 'users', permissionLevel: 'read' },
      },
      {
        title: 'API Keys',
        url: 'api-keys',
        element: PLATFORM.ApiKeys,
        visibility: 'tab',
        hasHeaderBorder: 'onScroll',
        permission: { permission: 'apiKeys', permissionLevel: 'read' },
        childPages: [
          {
            title: 'Create',
            url: 'create',
            element: PLATFORM.ApiKeysEdit,
            permission: { permission: 'apiKeys', permissionLevel: 'write' },
          },
          {
            title: 'Edit',
            url: 'edit/:id',
            element: PLATFORM.ApiKeysEdit,
            permission: { permission: 'apiKeys', permissionLevel: 'read' },
            dynamicBreadcrumb: true,
          },
        ],
      },
      {
        title: 'Webhooks',
        url: 'webhooks',
        element: PLATFORM.Webhooks,
        visibility: 'tab',
        hasHeaderBorder: 'onScroll',
        permission: { permission: 'webhooks', permissionLevel: 'write' },
        childPages: [
          {
            title: 'Create Webhook',
            url: 'edit',
            element: PLATFORM.WebhooksEdit,
          },
          {
            title: 'Create Webhook',
            url: 'edit/:URLType',
            element: PLATFORM.WebhooksEdit,
          },
          {
            title: 'Webhook',
            url: 'edit/:URLType/:id',
            element: PLATFORM.WebhooksEdit,
            permission: { permission: 'webhooks', permissionLevel: 'write' },
            dynamicBreadcrumb: true,
          },
        ],
      },
    ],
  },
  {
    title: 'User',
    url: '/profile',
    rules: {
      allowWithoutPlatformAccess: true,
    },
    visibility: 'sidebar',
    childPages: [
      {
        title: 'Personal',
        url: '',
        element: USER.Personal,
        showInBreadcrumbs: false,
      },
      {
        title: 'Notifications',
        url: 'notifications',
        element: USER.Notifications,
        hasHeaderBorder: true,
        rules: { requireFeatureFlag: FeatureFlag.EnableNotificationConfiguration },
      },
    ],
  },
  {
    title: 'Transfers',
    url: '/transfers',
    element: TRANSFERS.Transfers,
    visibility: 'sidebar',
    hasHeaderBorder: false,
    childPages: [
      { title: 'Check Transfer', url: 'edit/check/:id', element: TRANSFERS.TransfersCheck },
      { title: 'Realtime Transfer', url: 'edit/realtime/:id', element: TRANSFERS.TransfersRealtime },
      { title: 'Wire Transfer', url: 'edit/wire/:id', element: TRANSFERS.TransfersWire },
      { title: 'Wire Drawdown', url: 'edit/wire/drawdown/:id', element: TRANSFERS.TransfersWireDrawdown },
      {
        title: 'International Wire Transfer',
        url: 'edit/intl-wire/:id',
        element: TRANSFERS.TransfersInternationalWire,
      },
      { title: 'Book Transfer', url: 'edit/book/:id', element: TRANSFERS.TransfersBook },
      { title: 'Admin Transfer', url: 'edit/admin/:id', element: TRANSFERS.TransfersAdmin },
      { title: 'ACH Transfer', url: 'edit/ach/:id', element: TRANSFERS.TransfersAch },
      { title: 'Stablecoin Transfer', url: 'edit/crypto/:id', element: TRANSFERS.TransfersCrypto },
      { title: 'Create Transfer', url: 'edit/:type', element: TRANSFERS.TransfersAdd, hasHeaderBorder: 'onScroll' },

      { title: 'Check Transfer', url: 'check/:id', element: TRANSFERS.TransfersCheck },
      { title: 'Realtime Transfer', url: 'realtime/:id', element: TRANSFERS.TransfersRealtime },
      { title: 'Wire Transfer', url: 'wire/:id', element: TRANSFERS.TransfersWire },
      { title: 'Wire Drawdown', url: 'wire/drawdown/:id', element: TRANSFERS.TransfersWireDrawdown },
      { title: 'International Wire Transfer', url: 'intl-wire/:id', element: TRANSFERS.TransfersInternationalWire },
      { title: 'Book Transfer', url: 'book/:id', element: TRANSFERS.TransfersBook },
      { title: 'Admin Transfer', url: 'admin/:id', element: TRANSFERS.TransfersAdmin },
      { title: 'ACH Transfer', url: 'ach/:id', element: TRANSFERS.TransfersAch },
      { title: 'Stablecoin Transfer', url: 'crypto/:id', element: TRANSFERS.TransfersCrypto },
      { title: 'Create Transfer', url: ':type', element: TRANSFERS.TransfersAdd },
      {
        title: 'Transfer Templates',
        url: 'templates',
        element: TRANSFERS.TransferTemplatesOverview,
        permission: { permission: 'transferTemplates', permissionLevel: 'read' },
        hasHeaderBorder: 'onScroll',
        childPages: [
          {
            title: 'Edit',
            url: 'edit/:id',
            element: TRANSFERS.TransferTemplatesEdit,
            permission: { permission: 'transferTemplates', permissionLevel: 'read' },
          },
          {
            title: 'Review',
            url: 'review/:id',
            element: TRANSFERS.TransferTemplatesReview,
            permission: { permission: 'transferTemplates', permissionLevel: 'read' },
            hasHeaderBorder: 'onScroll',
          },
          {
            title: 'Create Template',
            url: 'create',
            element: TRANSFERS.TransferTemplatesEdit,
            permission: { permission: 'transferTemplates', permissionLevel: 'read' },
          },
        ],
      },
      {
        title: 'Pending Approvals',
        url: 'approvals',
        element: TRANSFERS.TransferApprovalsOverview,
        hasHeaderBorder: 'onScroll',
      },
      {
        title: 'Batches',
        url: 'batches',
        element: TRANSFERS.TransferBatchesOverview,
        hasHeaderBorder: false,
        childPages: [
          {
            title: 'Check Deposit',
            url: ':transferBatchId',
            element: TRANSFERS.TransferBatchesView,
          },
          {
            title: 'ACH',
            url: 'ach/:id',
            element: TRANSFERS.AchBatchView,
            rules: { requireFeatureFlag: FeatureFlag.EnableAchTransferBatches },
          },
          {
            // Resuming an unsent ACH batch reuses the create form (TransfersAdd). Nesting it under
            // Batches gives it a natural "Transfers › Batches › ACH" breadcrumb trail.
            title: 'ACH',
            url: 'edit/:type',
            element: TRANSFERS.TransfersAdd,
            rules: { requireFeatureFlag: FeatureFlag.EnableAchTransferBatches },
            hasHeaderBorder: 'onScroll',
          },
        ],
      },
    ],
  },
  {
    title: 'Loans',
    url: '/loans',
    element: LOANS.Loans,
    visibility: 'sidebar',
    hasHeaderBorder: false,
    permission: { permission: 'loans', permissionLevel: 'read' },
    childPages: [
      {
        title: 'Create Loan',
        url: 'create',
        element: LOANS.LoansCreate,
        permission: { permission: 'loans', permissionLevel: 'read' },
      },
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: LOANS.LoanSection,
        permission: { permission: 'loans', permissionLevel: 'read' },
        dynamicBreadcrumb: true,
        childPages: [
          { title: 'Information', url: '', element: LOANS.LoansEdit, visibility: 'tab', showInBreadcrumbs: false },
          {
            title: 'Collateral',
            url: 'collateral',
            element: LOANS.LoanCollateral,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          {
            title: 'Payments',
            url: 'payments',
            element: LOANS.LoanPayments,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          {
            title: 'Disbursements',
            url: 'disbursements',
            element: LOANS.LoanDisbursements,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          { title: 'Create Disbursement', url: 'disbursement', element: LOANS.LoansDisbursementCreate },
          { title: 'Create Payment', url: 'payment', element: LOANS.LoansPaymentCreate },
        ],
      },
      {
        title: 'Disbursements',
        url: 'disbursements',
        element: LOANS.LoansDisbursementOverview,
        hasHeaderBorder: false,
        permission: { permission: 'loans', permissionLevel: 'read' },
        childPages: [
          {
            title: 'Loan Disbursement',
            url: 'edit/:id',
            element: LOANS.LoansDisbursementEdit,
            permission: { permission: 'loans', permissionLevel: 'read' },
          },
          { title: 'Create Disbursement', url: 'create', element: LOANS.LoansDisbursementCreate },
        ],
      },
      {
        title: 'Payments',
        url: 'payments',
        element: LOANS.LoansPaymentOverview,
        hasHeaderBorder: false,
        permission: { permission: 'loans', permissionLevel: 'read' },
        childPages: [
          {
            title: 'Loan Payment',
            url: 'edit/:id',
            element: LOANS.LoansPaymentView,
            permission: { permission: 'loans', permissionLevel: 'read' },
          },
          {
            title: 'Create Payment',
            url: 'create',
            element: LOANS.LoansPaymentCreate,
            permission: { permission: 'loans', permissionLevel: 'read' },
          },
        ],
      },
      {
        title: 'Loan Sale',
        url: 'sales/:id',
        element: LOANS.LoansSale,
        permission: { permission: 'loans', permissionLevel: 'read' },
      },
    ],
  },
  {
    title: 'Accounts',
    url: '/accounts',
    element: BANK_ACCOUNTS.BankAccounts,
    visibility: 'sidebar',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'Account',
        url: 'edit/:id',
        routeElement: BANK_ACCOUNTS.BankAccountSection,
        dynamicBreadcrumb: true,
        childPages: [
          {
            title: 'Transfers',
            url: '',
            element: BANK_ACCOUNTS.BankAccountsTransfers,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          {
            title: 'Edit',
            url: 'info',
            element: BANK_ACCOUNTS.BankAccountsEdit,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          {
            title: 'Statements',
            url: 'statements',
            element: BANK_ACCOUNTS.BankAccountsStatements,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          {
            title: 'Documents',
            url: 'documents',
            element: BANK_ACCOUNTS.BankAccountsDocuments,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
          {
            title: 'Positive Pay Rules',
            url: 'ach-positive-pay',
            element: BANK_ACCOUNTS.BankAccountsACHPositivePay,
            visibility: 'tab',
            showInBreadcrumbs: false,
          },
        ],
      },
      { title: 'Create Account', url: 'edit', element: BANK_ACCOUNTS.BankAccountsEdit },
      {
        title: 'Interest Payout',
        url: 'interest-payouts/:id',
        element: BANK_ACCOUNTS.BankAccountsInterestPayoutView,
      },
      {
        title: 'Counterparties',
        url: 'counterparties',
        element: COUNTERPARTIES.Counterparties,
        hasHeaderBorder: false,
        permission: { permission: 'counterparties', permissionLevel: 'read' },
        childPages: [
          {
            title: 'Edit',
            url: 'edit/:id',
            element: COUNTERPARTIES.CounterpartiesEdit,
            permission: { permission: 'counterparties', permissionLevel: 'read' },
          },
          { title: 'Create Counterparty', url: 'edit', element: COUNTERPARTIES.CounterpartiesEdit },
        ],
      },
    ],
  },
  {
    title: 'Entities',
    url: '/entities',
    element: ENTITIES.Entities,
    visibility: 'sidebar',
    hasHeaderBorder: 'onScroll',
    childPages: [
      {
        title: 'Root Entity',
        url: 'root-entity',
        routeElement: ENTITIES.RootEntitySection,
        element: ENTITIES.RootEntityRedirect,
        permission: { permission: 'entities', permissionLevel: 'read' },
        childPages: [
          {
            title: 'Entity Details',
            url: 'live',
            element: ENTITIES.RootEntity,
            visibility: 'tab',
            showInBreadcrumbs: false,
            permission: { permission: 'entities', permissionLevel: 'read' },
          },
          {
            title: 'Accounts',
            url: 'live/accounts',
            element: ENTITIES.RootEntityAccounts,
            visibility: 'tab',
            showInBreadcrumbs: false,
            permission: { permission: 'bankAccounts', permissionLevel: 'read' },
          },
          {
            title: 'Entity Details',
            url: 'sandbox',
            element: ENTITIES.RootEntity,
            visibility: 'tab',
            showInBreadcrumbs: false,
            permission: { permission: 'entities', permissionLevel: 'read' },
          },
          {
            title: 'Accounts',
            url: 'sandbox/accounts',
            element: ENTITIES.RootEntityAccounts,
            visibility: 'tab',
            showInBreadcrumbs: false,
            permission: { permission: 'bankAccounts', permissionLevel: 'read' },
          },
        ],
      },
      {
        title: 'Entity',
        url: 'edit/:type/:id',
        routeElement: ENTITIES.EntitySection,
        permission: { permission: 'entities', permissionLevel: 'read' },
        dynamicBreadcrumb: true,
        childPages: [
          {
            title: 'Entity Details',
            url: '',
            element: ENTITIES.EntitiesEdit,
            visibility: 'tab',
            showInBreadcrumbs: false,
            permission: { permission: 'entities', permissionLevel: 'read' },
          },
          {
            title: 'Accounts',
            url: 'accounts',
            element: ENTITIES.EntityAccounts,
            visibility: 'tab',
            showInBreadcrumbs: false,
            permission: { permission: 'bankAccounts', permissionLevel: 'read' },
          },
        ],
      },
      {
        title: 'Create Entity',
        url: 'edit/:type',
        element: ENTITIES.EntitiesEdit,
        permission: { permission: 'entities', permissionLevel: 'write' },
      },
    ],
  },
  {
    title: 'Documents',
    url: '/documents',
    visibility: 'sidebar',
    childPages: [
      {
        title: 'Statements',
        url: 'statements',
        element: DOCUMENTS.Statements,
        visibility: 'tab',
        showInBreadcrumbs: false,
        hasHeaderBorder: 'onScroll',
        childPages: [
          {
            title: 'View',
            url: ':id',
            element: DOCUMENTS.DocumentView,
            permission: { permission: 'reporting', permissionLevel: 'read' },
            dynamicBreadcrumb: true,
          },
        ],
      },
      {
        title: 'Reports',
        url: 'reporting',
        element: DOCUMENTS.Reporting,
        visibility: 'tab',
        hasHeaderBorder: false,
        permission: { permission: 'reporting', permissionLevel: 'read' },
        childPages: [
          { title: 'Schedule Report', url: 'schedule', element: DOCUMENTS.ReportingSchedule },
          {
            title: 'View',
            url: ':id',
            element: DOCUMENTS.DocumentView,
            permission: { permission: 'reporting', permissionLevel: 'read' },
            dynamicBreadcrumb: true,
          },
        ],
      },
    ],
  },
  { title: 'Documents', url: '/documents', element: DOCUMENTS.Documents },
  {
    title: 'Events',
    url: '/events',
    element: PageEvents,
    visibility: 'sidebar',
    hasHeaderBorder: false,
    permission: { permission: 'webhooks', permissionLevel: 'read' },
    childPages: [
      {
        title: 'Webhook',
        url: 'webhook',
        element: PageWebhookEvents,
        hasHeaderBorder: false,
        permission: { permission: 'webhooks', permissionLevel: 'read' },
      },
    ],
  },
  {
    title: 'Card Programs',
    url: '/cards/programs',
    element: CARD_PROGRAMS.CardPrograms,
    visibility: 'hidden',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: CARD_PROGRAMS.CardProgramSection,
        childPages: [
          { title: 'Information', url: '', element: CARD_PROGRAMS.CardProgramInformation, visibility: 'tab' },
          { title: 'Actions', url: 'actions', element: CARD_PROGRAMS.CardProgramActions, visibility: 'tab' },
        ],
      },
    ],
  },
  {
    title: 'Cards',
    url: '/cards',
    element: CARDS.Cards,
    visibility: 'hidden',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: CARDS.CardSection,
        childPages: [
          { title: 'Information', url: '', element: CARDS.CardEdit, visibility: 'tab' },
          { title: 'Tokens', url: 'tokens', element: CARDS.CardTokens, visibility: 'tab' },
          { title: 'Transactions', url: 'transactions', element: CARDS.CardTransactions, visibility: 'tab' },
          { title: 'Events', url: 'events', element: CARDS.CardEvents, visibility: 'tab' },
          { title: 'Actions', url: 'actions', element: CARDS.CardActions, visibility: 'tab' },
          { title: 'API Response', url: 'api-response', element: CARDS.CardApiResponse, visibility: 'tab' },
        ],
      },
    ],
  },
  {
    title: 'Card Tokens',
    url: '/cards/tokens',
    element: CARD_TOKENS.CardTokens,
    visibility: 'hidden',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: CARD_TOKENS.CardTokenSection,
        childPages: [
          { title: 'Information', url: '', element: CARD_TOKENS.CardTokenInformation, visibility: 'tab' },
          { title: 'Events', url: 'events', element: CARD_TOKENS.CardTokenEvents, visibility: 'tab' },
          { title: 'Actions', url: 'actions', element: CARD_TOKENS.CardTokenActions, visibility: 'tab' },
          { title: 'API Response', url: 'api-response', element: CARD_TOKENS.CardTokenApiResponse, visibility: 'tab' },
        ],
      },
    ],
  },
  {
    title: 'Card Accounts',
    url: '/cards/accounts',
    element: CARD_ACCOUNTS.CardAccounts,
    visibility: 'hidden',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: CARD_ACCOUNTS.CardAccountSection,
        childPages: [
          { title: 'Information', url: '', element: CARD_ACCOUNTS.CardAccountInformation, visibility: 'tab' },
          { title: 'Cards', url: 'cards', element: CARD_ACCOUNTS.CardAccountCards, visibility: 'tab' },
          { title: 'Events', url: 'events', element: CARD_ACCOUNTS.CardAccountEvents, visibility: 'tab' },
          { title: 'Actions', url: 'actions', element: CARD_ACCOUNTS.CardAccountActions, visibility: 'tab' },
          {
            title: 'API Response',
            url: 'api-response',
            element: CARD_ACCOUNTS.CardAccountApiResponse,
            visibility: 'tab',
          },
        ],
      },
    ],
  },
  {
    title: 'Card Transactions',
    url: '/cards/transactions',
    element: CARD_TRANSACTIONS.CardTransactions,
    visibility: 'hidden',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: CARD_TRANSACTIONS.CardTransactionSection,
        childPages: [
          {
            title: 'Information',
            url: '',
            element: CARD_TRANSACTIONS.CardTransactionInformation,
            visibility: 'tab',
          },
          {
            title: 'API Response',
            url: 'api-response',
            element: CARD_TRANSACTIONS.CardTransactionApiResponse,
            visibility: 'tab',
          },
        ],
      },
    ],
  },
  {
    title: 'Card Disputes',
    url: '/cards/disputes',
    element: CARD_DISPUTES.CardDisputes,
    visibility: 'hidden',
    hasHeaderBorder: false,
    childPages: [
      {
        title: 'New',
        url: 'new',
        element: CARD_DISPUTES.CardDisputeNew,
        visibility: 'hidden',
      },
      {
        title: 'Edit',
        url: 'edit/:id',
        routeElement: CARD_DISPUTES.CardDisputeSection,
        childPages: [
          {
            title: 'Information',
            url: '',
            element: CARD_DISPUTES.CardDisputeInformation,
            visibility: 'tab',
          },
          { title: 'Evidence', url: 'evidence', element: CARD_DISPUTES.CardDisputeEvidence, visibility: 'tab' },
          {
            title: 'API Response',
            url: 'api-response',
            element: CARD_DISPUTES.CardDisputeApiResponse,
            visibility: 'tab',
          },
        ],
      },
    ],
  },
  {
    title: 'Contact',
    url: '/contact',
    element: PageContact,
  },
  {
    title: 'Commercial Deposit Account Agreement',
    url: '/legal/commercial-deposit-account-agreement',
    element: LEGAL.CommercialDepositAccountAgreement,
  },
  {
    title: 'Column Sweep Program Deposit Placement and Custodial Agreements',
    url: '/legal/column-sweep-program-deposit-placement-custodial-agreements',
    element: LEGAL.SweepProgramDepositPlacementCustodialAgreements,
  },
];
